import { allRules, PaxRule } from "@/config/rules";
import { DirectionEnum, PassengerTypeEnum, PaxRuleEnum } from "@/models/enums";
import { BookingState, DepartureState, PassengerState } from "@/models/www/store/BookingState";
import { useBookingStore } from "@/store/booking";

export let violatedPaxRules = [] as PaxRule[];

const clearViolatedPaxRules = () => {
    violatedPaxRules = [] as PaxRule[];
}

const doesViolatedPaxRuleExist = (paxRule: PaxRule) => violatedPaxRules.filter(r => r === paxRule).length > 0;
const addViolatedPaxRule = (paxRule: PaxRule) => {
    if (doesViolatedPaxRuleExist(paxRule)) {
        return;
    }

    violatedPaxRules.push(paxRule);
}

const getPaxRules = (booking: BookingState): PaxRule[] => {
    const route = booking.outward.route;

    let rules = allRules.find(x => x.modeOfTravel == booking.modeOfTravel)?.paxRules;
    return rules = rules?.filter(x => x.routes.filter(r => r.code === route).length > 0) ?? [] as PaxRule[];
}

const getMaxAmountPaxRule = (booking: BookingState) => getPaxRules(booking)
                                                        .find(x => x.type == PaxRuleEnum.MaxAmount);
const getPassengerTypeMaxAmountPaxRules = (booking: BookingState) => getPaxRules(booking)
                                                                        .filter(x => x.type == PaxRuleEnum.MaxAmountPassengerType);
const getMandatoryPaxRules = (booking: BookingState) => getPaxRules(booking)
                                                            .filter(x => x.type === PaxRuleEnum.Mandatory);
const getForbiddenPaxRules = (booking: BookingState) => getPaxRules(booking)
                                                            .filter(x => x.type === PaxRuleEnum.Forbidden);
const getMaxPerAdultPaxRules = (booking: BookingState) => getPaxRules(booking)
                                                            .filter(x => x.type === PaxRuleEnum.MaxPerAdult);

const isSingle = (booking: BookingState) => booking.tripType === DirectionEnum.Outward;

//Date validation
const hasDates = (booking: BookingState) => booking.outward.date && (isSingle(booking) || booking.return.date);

//Passenger validation 
const isValidMaxPerAdult = (paxRule: PaxRule, passengers: PassengerState[]) => {
    const passengerTypes = passengers.map(x => x.type);
    const passenger = (paxRule.passengerTypes?.find(p => passengerTypes.includes(p.type)));

    if (passenger) {
        const adults = passengers.filter(p => p.type === PassengerTypeEnum.Adult || 
                                                p.type === PassengerTypeEnum.ShareholderAdult ).length;

        const passengerAmount = passengers.filter(p => p.type === passenger.type).length;

        if (paxRule.amount) {
            const allowedAmount = adults * paxRule.amount;
            const violated = passengerAmount > allowedAmount;

            if (violated) {
                addViolatedPaxRule(paxRule);
                return false;
            }
        }
    }

    return true;
}

const isValidMaxAmountPassengerType = (paxRule: PaxRule, passengers: PassengerState[]) => {
    const passengerTypes = passengers.map(x => x.type);
    const passenger = paxRule.passengerTypes?.find(p => passengerTypes.includes(p.type));

    if (passenger) {
        const passengerAmount = passengers.filter(p => p.type === passenger.type).length;
        const violated = passengerAmount > (paxRule.amount ?? 0);

        if (violated) {
            addViolatedPaxRule(paxRule);
            return false;
        }
    }

    return true;
}

const isValidMandatoryPassenger = (passengers: PassengerState[], paxRule: PaxRule) => {
    const passengerTypes = passengers.map(x => x.type);
    const mandatoryPassengerExist = (paxRule.passengerTypes?.filter(p => passengerTypes.includes(p.type)).length ?? 0) > 0;

    if (!mandatoryPassengerExist) {
        addViolatedPaxRule(paxRule);
        return false;
    }

    return true;
}

const isPassengerForbidden = (paxRule: PaxRule, passengers: PassengerState[]) => {
    const passengerTypes = passengers.map(x => x.type);
    const hasForbiddenPassengers = (paxRule.passengerTypes?.filter(p => passengerTypes.includes(p.type)).length ?? 0) > 0;

    if (hasForbiddenPassengers) {
        addViolatedPaxRule(paxRule);
        return true;
    }

    return false;
}

const hasMandatoryPassengers = (booking: BookingState) => {
    const mandatoryPaxRules = getMandatoryPaxRules(booking);
    return !(mandatoryPaxRules.filter(r => isValidMandatoryPassenger(booking.outward.passengers, r) == false).length > 0);
}

const hasForbiddenPassengers = (booking: BookingState) => {
    const forbiddenPaxRules = getForbiddenPaxRules(booking);
    return !(forbiddenPaxRules.filter(r => isPassengerForbidden(r, booking.outward.passengers) == true).length > 0)
}

const hasValidMaxPerAdult = (booking: BookingState) => {
    const maxPerAdultPaxRules = getMaxPerAdultPaxRules(booking);
    return !(maxPerAdultPaxRules.filter(r => isValidMaxPerAdult(r, booking.outward.passengers) == false).length > 0)
}

const hasValidPassengerTypeAmount = (booking: BookingState) => {
    const maxAmountPassTypePaxRule = getPassengerTypeMaxAmountPaxRules(booking);
    return !(maxAmountPassTypePaxRule.filter(r => isValidMaxAmountPassengerType(r, booking.outward.passengers) == false).length > 0);
}

const hasValidPassengerAmount = (booking: BookingState) => {
    const maxAmountPaxRule = getMaxAmountPaxRule(booking);
    const inValidAmount = booking.outward.passengers.length > (maxAmountPaxRule?.amount ?? 0);

    if (inValidAmount && maxAmountPaxRule) {
        addViolatedPaxRule(maxAmountPaxRule);
        return false;
    }

    return true;
};

const hasPassengers = (booking: BookingState) => {
    if (booking.outward.passengers.length === 0) {
        addViolatedPaxRule({
            text: "Passengers is required"
        } as PaxRule);

        return false;
    }

    return true;
}

//Departure validation
const validDepartures = (booking: BookingState) => booking.outward.departure.id.length > 0 && 
                                                    booking.return.departure.id.length > 0 || 
                                                    isSingle(booking);
export const validDeparture = (departure: DepartureState): boolean => departure.id.length > 0;

export const validPassengers = (booking: BookingState): boolean => {
    clearViolatedPaxRules();

    return hasPassengers(booking) && 
            hasValidPassengerAmount(booking) && 
            hasForbiddenPassengers(booking) && 
            hasMandatoryPassengers(booking) && hasValidMaxPerAdult(booking) && 
            hasValidPassengerTypeAmount(booking);
}

const useBookingValidation = () => {
    const bookingStore = useBookingStore();

    const step1IsValid = (booking: BookingState) => {
        return [validPassengers, hasDates].every(f => f(booking));
    }

    const step2IsValid = (booking: BookingState) => {
        const trips = booking.tripType === DirectionEnum.Return ? [DirectionEnum.Outward, DirectionEnum.Return] : [DirectionEnum.Outward];
        const getPassengers = (direction: DirectionEnum) => direction === DirectionEnum.Outward ? booking.outward.passengers : booking.return.passengers;
        return [validDepartures].every(f => f(booking)) && trips.every(t => getPassengers(t));
    }

    const stepIsValid = (step: number) => {
        const booking = bookingStore.getBooking;
        switch (step) {
            case 1: return step1IsValid(booking);
            case 2: return step2IsValid(booking);
            case 3:
            case 4:
            case 5: return true;
            default: return false;
        }
    }

    return { stepIsValid, validPassengers, hasForbiddenPassengers, hasValidMaxPerAdult }
}

export default useBookingValidation;